// «Cancelar venta» del vendedor dentro del wizard: cuándo se ofrece, el mensaje al cancelar y qué
// hacer ante cada error de POST /wizard/sessions/{id}/cancel.
import { RequestTimeoutError } from '@/api/client';
import { ApiError } from '@/api/errors';
import type { CancelSessionResult } from '@/api/services/retail-wizard';
import { formatMXN } from '@/shared/lib/format/money';
import type { WizardSession, WizardSessionState } from './types';

/** Tope del motivo que acepta el silo. */
export const CANCEL_REASON_MAX_LENGTH = 200;

const OPEN_STATES: readonly WizardSessionState[] = [
    'ACTIVE',
    'PAUSED_EXTERNAL_REFERRAL',
    'PAUSED_CURP_CONFLICT',
];

/** Solo una venta abierta se cancela: con `saleId` el servidor ya la cerró. */
export function canCancelSale(
    session: Pick<WizardSession, 'state' | 'saleId'> | undefined,
): boolean {
    if (!session) return false;
    return OPEN_STATES.includes(session.state) && !session.saleId;
}

export function isValidCancelReason(reason: string): boolean {
    const trimmed = reason.trim();
    return trimmed !== '' && trimmed.length <= CANCEL_REASON_MAX_LENGTH;
}

const STILL_RELEASING =
    'Algunos productos apartados todavía se están liberando; regresan al inventario en unos minutos.';

/** Con enganche cobrado avisa el reembolso; con líneas aún liberándose, que tardan en regresar. */
export function cancelSaleSuccessMessage(result: CancelSessionResult | null | undefined): string {
    const refund = result?.refundPendingCents ?? 0;
    const releasing = (result?.releasingLines ?? 0) > 0 ? ` ${STILL_RELEASING}` : '';
    if (refund <= 0) return `Venta cancelada.${releasing}`;
    return `Venta cancelada. Se registró un reembolso pendiente de ${formatMXN(refund / 100)}. Lo encuentras en Reembolsos de tu portal para entregarlo.${releasing}`;
}

const RETURNS_TO_INVENTORY = 'Los productos apartados se liberan para regresar al inventario.';

/** Qué pasa al cancelar ESTA venta. `locked` = contado ya cobrado: solo la cancela el administrador. */
export interface CancelSaleImpact {
    locked: boolean;
    message: string;
}

/** El cobro registrado en la sesión es el mismo monto que el silo deja como reembolso pendiente. */
export function cancelSaleImpact(
    session: Pick<WizardSession, 'paymentMethodKind' | 'loan' | 'payment'> | undefined,
): CancelSaleImpact {
    const paidCents = session?.payment?.amountCents ?? 0;
    if (session?.paymentMethodKind === 'CASH_OR_CARD' && paidCents > 0) {
        return {
            locked: true,
            message:
                'Esta venta ya tiene el cobro registrado y no se puede cancelar desde aquí. Si el cliente se arrepiente, el administrador la cancela desde Ventas.',
        };
    }
    const hasCredit = Boolean(session?.loan?.creditId);
    const refund = formatMXN(paidCents / 100);
    let impact: string;
    if (paidCents > 0) {
        impact = hasCredit
            ? `Se descarta el crédito y queda un reembolso pendiente de ${refund} para el cliente.`
            : `Queda un reembolso pendiente de ${refund} para el cliente.`;
    } else {
        impact = hasCredit
            ? 'Se descarta el crédito. No hay nada que reembolsar.'
            : 'No hay nada que reembolsar.';
    }
    return { locked: false, message: `${impact} ${RETURNS_TO_INVENTORY}` };
}

/** `delivery` = seguir con la venta (Entrega o Referidos) · `retry` = repetir la cancelación · `reload` = releer la venta. */
export type CancelSaleAction = 'delivery' | 'retry' | 'reload';

export const CANCEL_SALE_ACTION_LABEL: Record<CancelSaleAction, string> = {
    delivery: 'Continuar la venta',
    retry: 'Reintentar',
    reload: 'Recargar',
};

const MESSAGE_BY_CODE: Record<string, string> = {
    CREDIT_NOT_PENDING:
        'El crédito de esta venta ya se activó y ya no se puede cancelar. Sigue con la entrega.',
    PAID_SALE_LOCKED:
        'Esta venta ya tiene el cobro registrado y no se puede cancelar desde aquí. Termínala; si el cliente se arrepiente, el administrador la cancela desde Ventas.',
    EXTERNAL_INTEGRATION_ERROR:
        'El sistema de crédito no respondió y la venta no se canceló. Espera unos segundos y vuelve a intentar.',
    INVALID_SESSION_STATE: 'Esta venta ya cambió de estado. Recarga para ver cómo quedó.',
    SESSION_CONCURRENT_UPDATE:
        'Alguien más acaba de modificar esta venta. Recarga y vuelve a intentar.',
    SESSION_NOT_FOUND: 'Esta venta ya no existe. Vuelve al portal.',
    PERMISSION_DENIED: 'No tienes permiso para cancelar ventas en esta tienda.',
    VALIDATION_ERROR: `El motivo no es válido. Escribe uno de máximo ${String(CANCEL_REASON_MAX_LENGTH)} caracteres.`,
};

const ACTION_BY_CODE: Record<string, CancelSaleAction> = {
    CREDIT_NOT_PENDING: 'delivery',
    EXTERNAL_INTEGRATION_ERROR: 'retry',
    INVALID_SESSION_STATE: 'reload',
    SESSION_CONCURRENT_UPDATE: 'reload',
};

const FALLBACK = 'No se pudo cancelar la venta. Vuelve a intentar en unos segundos.';

export function cancelSaleErrorMessage(error: unknown): string {
    if (error instanceof RequestTimeoutError) {
        return 'La cancelación tardó demasiado y la venta pudo quedar cancelada. Recarga para ver cómo quedó.';
    }
    if (!(error instanceof ApiError)) return FALLBACK;
    const known = MESSAGE_BY_CODE[error.code] as string | undefined;
    if (known !== undefined) return known;
    return error.msg !== '' ? error.msg : FALLBACK;
}

/** Acción que resuelve el error; `undefined` si basta con el mensaje. */
export function cancelSaleAction(error: unknown): CancelSaleAction | undefined {
    if (error instanceof RequestTimeoutError) return 'reload';
    if (!(error instanceof ApiError) || !Object.hasOwn(ACTION_BY_CODE, error.code))
        return undefined;
    return ACTION_BY_CODE[error.code];
}
